import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { auth, db } from "../Firebase";
import { doc, getDoc } from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import html2pdf from "html2pdf.js";
import "../styles/CertificatePreview.css";

const CertificatePreview = () => {
  const { courseId } = useParams();

  const [certificate, setCertificate] = useState(null);
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [signatureLoaded, setSignatureLoaded] = useState(false);
  const [signatureDataUrl, setSignatureDataUrl] = useState(null);

  const loadImageAsBase64 = async (url) => {
    const res = await fetch(url);
    const blob = await res.blob();
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.readAsDataURL(blob);
    });
  };

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (user) => {
      if (!user || !courseId) {
        setLoading(false);
        return;
      }

      try {
        const certRef = doc(
          db,
          "certificates",
          `${courseId}_${user.uid}`
        );

        const certSnap = await getDoc(certRef);
        if (!certSnap.exists()) {
          setLoading(false);
          return;
        }

        const certData = certSnap.data();
        setCertificate(certData);

        if (certData.teacherSignature) {
          const base64 = await loadImageAsBase64(
            certData.teacherSignature
          );
          setSignatureDataUrl(base64);
          setSignatureLoaded(true);
        }

        const courseSnap = await getDoc(doc(db, "courses", courseId));
        if (courseSnap.exists()) {
          setCourse(courseSnap.data());
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    });

    return () => unsub();
  }, [courseId]);

  const handleDownload = () => {
    const element = document.getElementById("certificate");
    if (!element || !signatureLoaded) return;

    html2pdf().set({
      margin: 0,
      filename: `Certificate-${certificate.certificateNumber}.pdf`,
      image: { type: "jpeg", quality: 1 },
      html2canvas: { scale: 3 },
      jsPDF: {
        unit: "in",
        format: "a4",
        orientation: "landscape"
      }
    }).from(element).save();
  };

  if (loading) return <p>Loading certificate…</p>;
  if (!certificate) return <p>Certificate not available</p>;

  /* ================= UI ================= */
  return (
    <div className="certificate-page">
      <div id="certificate" className="certificate-container">
        <div className="certificate-watermark">Vidify</div>

        <p className="issuer">VIDIFY LEARNING PLATFORM</p>
        <h1 className="title">CERTIFICATE</h1>
        <p className="subtitle">OF COMPLETION</p>

        <p className="present-text">
          This certificate is proudly presented to
        </p>

        <h2 className="student-name">{certificate.studentName}</h2>

        <p className="course-text">
          for successfully completing the course
        </p>

        <h3 className="course-name">
          {certificate.courseName || course?.title}
        </h3>

        <div className="certificate-footer">
          <div className="teacher-section">
            <p className="teacher-name">
              {certificate.teacherName}
            </p>
            <span className="teacher-role">Instructor</span>

            {certificate.teacherSignature && (
              <img
                src={certificate.teacherSignature}
                alt="Instructor Signature"
                className="signature"
                onLoad={() => setSignatureLoaded(true)}
              />
            )}
          </div>

          <div className="date-section">
            <p>Issued on</p>
            <strong>
              {certificate.issuedAt?.toDate()
                .toLocaleDateString("en-GB")
                .replace(/\//g, "-")}
            </strong>

            <p className="certificate-number">
              Certificate No: {certificate.certificateNumber}
            </p>
          </div>
        </div>
      </div>

      <div className="certificate-dnld-btn">
        <button
          className="download-btn"
          onClick={handleDownload}
          disabled={!signatureLoaded}
        >
          {signatureLoaded
            ? "Download Certificate (PDF)"
            : "Preparing Certificate…"}
        </button>
      </div>
    </div>
  );
};

export default CertificatePreview;
